import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft, Upload, X, Send, MapPin, ChefHat,
  CalendarDays, Clock, Utensils, Package, Link2, FileText, Loader,
} from "lucide-react";
import { uploadFiles } from "../../services/upload.api";
import { fetchKitchenById } from "../../services/catalog.api";
import { createWish } from "../../services/wish.api";
import NearestKitchenSeller from "../kitchen-profile/NearestKitchenSeller/NearestKitchenSeller";
import "./WishOrderForm.css";

const unitOptions = [
  { value: "piece", label: "Piece" },
  { value: "plate", label: "Plate" },
  { value: "kg", label: "Kg" },
  { value: "litre", label: "Litre" },
];

const fetchKitchenProfile = (kitchenId) => fetchKitchenById(kitchenId);

const initialFormData = {
  dishName: "",
  desiredDate: "",
  desiredTime: "",
  quantity: 2,
  unit: "piece",
  videoLink: "",
  ingredients: "",
  additionalInfo: "",
  // The negotiation always opens with the customer's own offer.
  offerPrice: 500,
};

const inputStyle = {
  onFocus: (e) => { e.target.style.borderColor = "#0d6efd"; e.target.style.background = "#fff"; },
  onBlur:  (e) => { e.target.style.borderColor = "#e5e7eb"; e.target.style.background = "#f9fafb"; },
};

export default function WishOrderForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sellerId = searchParams.get("sellerId");

  const [data, setData] = useState(initialFormData);
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: "", text: "" });
  const dropRef = useRef(null);

  const { data: kitchenData, isPending: isKitchenPending, error: kitchenError } = useQuery({
    queryKey: ["kitchenProfile", sellerId],
    queryFn: () => fetchKitchenProfile(sellerId),
    enabled: !!sellerId,
  });

  useEffect(() => {
    const urls = selectedImages.map((img) =>
      img instanceof File ? URL.createObjectURL(img) : img,
    );
    setPreviewUrls(urls);
    return () => urls.forEach((url, i) => { if (selectedImages[i] instanceof File) URL.revokeObjectURL(url); });
  }, [selectedImages]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const appendImages = (files) => {
    const images = (files || []).filter((f) => f.type?.startsWith("image/"));
    if (images.length) setSelectedImages((prev) => [...prev, ...images]);
  };

  const handleFileChange = (e) => {
    appendImages(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const handleImageDelete = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    appendImages(Array.from(e.dataTransfer.files || []));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage({ type: "", text: "" });
    if (!sellerId) {
      setSubmitMessage({ type: "error", text: "Seller is missing. Please open this page from a kitchen profile." });
      return;
    }
    try {
      setIsSubmitting(true);
      // Reference photos must be URLs this platform issued, so every picked
      // file goes through POST /uploads first.
      const uploadedPhotoUrls = await uploadFiles(
        selectedImages.filter((img) => img instanceof File),
      );

      await createWish({
        chefId: sellerId,
        dishName: data.dishName.trim(),
        desiredDate: data.desiredDate,
        timeSlot: data.desiredTime,
        referencePhotos: uploadedPhotoUrls,
        referenceLink: data.videoLink.trim() || undefined,
        quantity: Number(data.quantity),
        unit: data.unit,
        // The API takes a list; the form collects one comma-separated line.
        ingredients: data.ingredients
          .split(",")
          .map((i) => i.trim())
          .filter(Boolean),
        notes: data.additionalInfo || undefined,
        offerPrice: Number(data.offerPrice),
      });

      setData(initialFormData);
      setSelectedImages([]);
      setSubmitMessage({ type: "success", text: "Wish order submitted successfully!" });
      navigate("/my-wishes");
    } catch (err) {
      setSubmitMessage({
        type: "error",
        text: err?.message || "Submission failed. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const specialty    = kitchenData?.specialty?.length    ? kitchenData.specialty    : [];
  const restrictions = kitchenData?.restrictions?.length ? kitchenData.restrictions : [];

  return (
    <div className="home-content-shell page-navbar-space pb-5">

      {/* Back */}
      <Link
        to={sellerId ? `/SellerProfile/${sellerId}` : "/all-kitchens"}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          fontSize: 13, fontWeight: 600, color: "#6b7280",
          textDecoration: "none", fontFamily: "'Inter', sans-serif", marginBottom: 20,
        }}
      >
        <ArrowLeft size={15} /> Back to Kitchen
      </Link>

      <div className="wo-layout">

        {/* ── Sidebar: kitchen info ── */}
        <div className="wo-sidebar">

          <div className="wo-card">
            {isKitchenPending ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Skeleton circle width={72} height={72} />
                <Skeleton height={18} width="75%" />
                <Skeleton height={13} width="55%" />
                <Skeleton height={13} width="65%" />
              </div>
            ) : (
              <>
                <img
                  src={kitchenData?.kitchenLogo || "/Assets/Img/kitchen_default.png"}
                  alt={kitchenData?.name}
                  className="wo-avatar"
                  onError={e => { e.target.src = "/Assets/Img/kitchen_default.png"; }}
                />
                <h5 style={{ fontWeight: 800, fontSize: 16, margin: "12px 0 4px", color: "#0f172a", fontFamily: "'Inter', sans-serif" }}>
                  {kitchenData?.name || "Kitchen"}
                </h5>
                {kitchenData?.address?.text && (
                  <p style={{ margin: "0 0 4px", fontSize: 12, color: "#9ca3af", fontFamily: "'Inter', sans-serif", display: "flex", alignItems: "flex-start", gap: 4 }}>
                    <MapPin size={12} style={{ marginTop: 2, flexShrink: 0 }} />
                    {kitchenData.address.text}
                  </p>
                )}
                <p style={{ margin: 0, fontSize: 12, color: "#9ca3af", fontFamily: "'Inter', sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                  <ChefHat size={12} /> Accepts custom wish orders
                </p>
              </>
            )}

            {kitchenError && (
              <p style={{ margin: "10px 0 0", fontSize: 12, color: "#d97706", fontFamily: "'Inter', sans-serif" }}>
                Could not load kitchen details.
              </p>
            )}
          </div>

          {/* Specialty */}
          {(isKitchenPending || specialty.length > 0) && (
            <div className="wo-card">
              <p className="wo-section-title">Specialties</p>
              {isKitchenPending
                ? <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{[1,2,3].map(i => <Skeleton key={i} height={26} width={72} borderRadius={20} />)}</div>
                : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {specialty.map((s, i) => <span key={i} className="wo-pill wo-pill-blue">{s}</span>)}
                  </div>
                )
              }
            </div>
          )}

          {/* Restrictions */}
          {(isKitchenPending || restrictions.length > 0) && (
            <div className="wo-card">
              <p className="wo-section-title">Restrictions</p>
              {isKitchenPending
                ? <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{[1,2,3].map(i => <Skeleton key={i} height={26} width={80} borderRadius={20} />)}</div>
                : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {restrictions.map((r, i) => <span key={i} className="wo-pill wo-pill-red">{r}</span>)}
                  </div>
                )
              }
            </div>
          )}
        </div>

        {/* ── Main form ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Page title */}
          <div>
            <h4 style={{ fontWeight: 800, fontSize: 20, margin: "0 0 4px", color: "#0f172a", fontFamily: "'Inter', sans-serif" }}>
              Make a Wish Order
            </h4>
            <p style={{ margin: 0, fontSize: 13, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
              Describe the dish you'd like — the kitchen will review and send you a price.
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Dish + quantity */}
            <div className="wo-card">
              <p className="wo-section-title">Dish Details</p>
              <div className="wo-row wo-row-2" style={{ marginBottom: 14 }}>
                <div>
                  <label className="wo-label">
                    <Utensils size={10} style={{ marginRight: 4 }} />Dish Name *
                  </label>
                  <input
                    type="text"
                    name="dishName"
                    className="wo-input"
                    placeholder="e.g. Butter Chicken, Biriyani…"
                    value={data.dishName}
                    onChange={handleChange}
                    required
                    {...inputStyle}
                  />
                </div>
                <div>
                  <label className="wo-label">
                    <Package size={10} style={{ marginRight: 4 }} />Quantity *
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="number"
                      name="quantity"
                      className="wo-input"
                      min={2}
                      value={data.quantity}
                      onChange={handleChange}
                      style={{ flex: 1 }}
                      {...inputStyle}
                    />
                    <select
                      name="unit"
                      className="wo-select"
                      value={data.unit}
                      onChange={handleChange}
                      style={{ width: 90 }}
                    >
                      {unitOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="wo-row wo-row-2" style={{ marginBottom: 14 }}>
                <div>
                  <label className="wo-label">
                    <CalendarDays size={10} style={{ marginRight: 4 }} />Delivery Date *
                  </label>
                  <input
                    type="date"
                    name="desiredDate"
                    className="wo-input"
                    value={data.desiredDate}
                    onChange={handleChange}
                    required
                    {...inputStyle}
                  />
                </div>
                <div>
                  <label className="wo-label">
                    <Clock size={10} style={{ marginRight: 4 }} />Delivery Time *
                  </label>
                  <input
                    type="time"
                    name="desiredTime"
                    className="wo-input"
                    value={data.desiredTime}
                    onChange={handleChange}
                    required
                    {...inputStyle}
                  />
                </div>
              </div>

              {/* Opening offer — the negotiation always starts from the customer. */}
              <div>
                <label className="wo-label">
                  <Package size={10} style={{ marginRight: 4 }} />Your Offer (৳) *
                </label>
                <input
                  type="number"
                  name="offerPrice"
                  className="wo-input"
                  min={1}
                  value={data.offerPrice}
                  onChange={handleChange}
                  required
                  {...inputStyle}
                />
                <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
                  The kitchen can accept this or counter with its own price.
                </p>
              </div>
            </div>

            {/* Photos */}
            <div className="wo-card">
              <p className="wo-section-title">Reference Photos <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#b0b7c3" }}>— optional</span></p>
              <div
                className={`wo-drop${dragging ? " dragging" : ""}`}
                ref={dropRef}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById("wo-img-input")?.click()}
              >
                <input
                  id="wo-img-input"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />

                {selectedImages.length === 0 ? (
                  <>
                    <Upload size={28} color="#9ca3af" />
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#6b7280", fontFamily: "'Inter', sans-serif" }}>
                      Drag & drop photos here
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
                      or click to browse
                    </p>
                  </>
                ) : (
                  <div style={{ width: "100%" }} onClick={e => e.stopPropagation()}>
                    <div className="wo-drop-grid">
                      {previewUrls.map((url, idx) => (
                        <div key={idx} className="wo-drop-item">
                          <img src={url} alt={`preview-${idx + 1}`} />
                          <button
                            className="wo-drop-remove"
                            onClick={(e) => handleImageDelete(e, idx)}
                            title="Remove"
                          >
                            <X size={11} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                      <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
                        {selectedImages.length} photo{selectedImages.length !== 1 ? "s" : ""} selected
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setSelectedImages([]); }}
                        style={{ fontSize: 12, fontWeight: 600, color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontFamily: "'Inter', sans-serif" }}
                      >
                        Clear all
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ingredients + Additional info */}
            <div className="wo-card">
              <p className="wo-section-title">More Details</p>

              <div style={{ marginBottom: 14 }}>
                <label className="wo-label">
                  <FileText size={10} style={{ marginRight: 4 }} />
                  Suggested Ingredients <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#b0b7c3" }}>— optional</span>
                </label>
                <textarea
                  name="ingredients"
                  className="wo-textarea"
                  placeholder="List key ingredients you'd like included…"
                  value={data.ingredients}
                  onChange={handleChange}
                  {...inputStyle}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label className="wo-label">
                  <FileText size={10} style={{ marginRight: 4 }} />
                  Additional Info <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#b0b7c3" }}>— optional</span>
                </label>
                <textarea
                  name="additionalInfo"
                  className="wo-textarea"
                  placeholder="Allergies, spice level, serving style, or any other notes…"
                  value={data.additionalInfo}
                  onChange={handleChange}
                  {...inputStyle}
                />
              </div>

              <div>
                <label className="wo-label">
                  <Link2 size={10} style={{ marginRight: 4 }} />
                  Video / Reference Link <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#b0b7c3" }}>— optional</span>
                </label>
                <input
                  type="url"
                  name="videoLink"
                  className="wo-input"
                  placeholder="https://youtube.com/…"
                  value={data.videoLink}
                  onChange={handleChange}
                  {...inputStyle}
                />
              </div>
            </div>

            {/* Alert */}
            {submitMessage.text && (
              <div className={`wo-alert wo-alert-${submitMessage.type === "success" ? "success" : "error"}`}>
                {submitMessage.text}
              </div>
            )}

            {/* Submit */}
            <button type="submit" className="wo-submit" disabled={isSubmitting}>
              {isSubmitting
                ? <><Loader size={16} style={{ animation: "spin 1s linear infinite" }} /> Submitting…</>
                : <><Send size={16} /> Submit Wish Order</>
              }
            </button>

          </form>
        </div>
      </div>

      {/* Nearby kitchens */}
      <div style={{ marginTop: 56 }}>
        <NearestKitchenSeller />
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
